import React, { PropsWithChildren } from "react";
import { Form } from "react-bootstrap";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";

export interface StatefulFormProps extends PropsWithChildren {
    onSubmit?: (data: FieldValues) => void | Promise<void>;
    methods: UseFormReturn<FieldValues, any, FieldValues>;
    readonly?: boolean;
    className?: string;
    noSubmitOnEnter?: boolean;
}

/**
 *
 * @param {UseFormReturn<FieldValues, any, FieldValues>} methods Methods returned from the react-hook-form useForm() hook
 * @param onSubmit The function to call when the form is submitted
 * @returns A react-hook-form FormProvider component that renders a Form component with the onSubmit function passed to the useForm() hook.
 * The children of this component should include form input components that use the useFormContext hook.
 */
export const StatefulForm: React.FC<StatefulFormProps> = ({
    methods,
    onSubmit,
    readonly,
    className,
    children,
    noSubmitOnEnter,
}: StatefulFormProps) => {
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (noSubmitOnEnter && event.key === "Enter" && event.target instanceof HTMLInputElement) {
            event.preventDefault();
        }
    };

    return (
        <FormProvider {...methods}>
            <Form
                onSubmit={onSubmit ? methods.handleSubmit((data: unknown) => onSubmit(data as FieldValues)) : undefined}
                className={className}
                onKeyDown={handleKeyDown}
            >
                <fieldset disabled={readonly}>{children}</fieldset>
            </Form>
        </FormProvider>
    );
};
